(ns dre.components.caret
  (:require
   [dre.components.searchbar.events :as searchbar-events]
   [dre.components.searchbar.subs :as-alias searchbar-subs]
   [dre.page-util :as page-util]
   [dre.utils.bem :as bem]
   [re-frame.core :as rf]
   [reagent.core :as r]))

(def component-name :caret)

;; This canvas is for calculating caret-input width
(defonce ^:private canvas
  (.createElement js/document "canvas"))

;; Measures text width based on text and font spec
(def text-width
  (memoize
   (fn [text font]
     (let [context (.getContext canvas "2d")
           _ (set! (.-font context) font)
           metrics (.measureText context text)]
       (page-util/ceil (.-width metrics))))))

;; Calculates caret input's width based on its value and font spec
(def caret-input-width
  (memoize
   (fn [caret-input]
     (if (seq caret-input)
       (let [font "14px Arial"]
         (str (+ 4 (text-width caret-input font)) "px"))
       "4px"))))

(defn- on-esc
  [{:keys [db-root-path event]}]
  (rf/dispatch [::searchbar-events/set-caret-position db-root-path nil])
  (.preventDefault event))

(defn- on-left-right
  "Move caret to left or right only when new position is within limits and the
   input is empty."
  [{:keys [db-root-path event keycode caret-position pill-count]}]
  (let [op (case keycode
             37 dec
             39 inc)
        new-position (op caret-position)]
    (when (and (empty? (.. event -target -value))
               (>= new-position 0)
               (<= new-position pill-count))
      (rf/dispatch [::searchbar-events/set-caret-position db-root-path new-position]))))

;; to understand `keyboard-up-down-arrows-used?` see https://doctorevidence.atlassian.net/browse/SEARCH-168
(defn- on-up-down
  [{:keys [event keycode active-suggestion-idx suggestions keyboard-up-down-arrows-used?]}]
  (reset! keyboard-up-down-arrows-used? true)
  (let [op (case keycode
             38 dec
             40 inc)
        new-idx (op @active-suggestion-idx)]
    (when (and
           (<= 0 new-idx)
           (> (count @suggestions) new-idx))
      (reset! active-suggestion-idx new-idx))
    (.preventDefault event)))

(defn- on-del
  [{:keys [db-root-path pill-count caret-position keycode input-text event on-change]}]
  (when (and
         (empty? @input-text) ; input is empty
         (pos? pill-count)) ; pills are not empty
    (case keycode
      #_backspace 8 (when (pos? caret-position)
                      (rf/dispatch-sync [::searchbar-events/remove-pill db-root-path {:idx (dec caret-position)
                                                                                 :on-change on-change
                                                                                 :update-caret-op :dec}]))
      #_delete   46 (rf/dispatch-sync [::searchbar-events/remove-pill db-root-path {:idx caret-position
                                                                                    :on-change on-change}]))
    (.preventDefault event)))

(defn- on-space
  [_]
  )

;; to understand `keyboard-up-down-arrows-used?` see https://doctorevidence.atlassian.net/browse/SEARCH-168
(defn- on-enter
  [{:keys [db-root-path active-suggestion-idx
           on-submit input-text on-change suggestions keyboard-up-down-arrows-used?]}]
  (if (empty? @input-text)
    (on-submit :enter)
    (when (seq @suggestions)
      (rf/dispatch-sync [::searchbar-events/insert-pill db-root-path
                         {:pill (if @keyboard-up-down-arrows-used?
                                  (nth @suggestions @active-suggestion-idx)
                                  (first @suggestions))
                          :on-change on-change}])
      (rf/dispatch [::searchbar-events/clear-suggestions db-root-path])))
  (reset! active-suggestion-idx 0)
  (reset! keyboard-up-down-arrows-used? false))

(def keycode->handler
  {27 on-esc        ; esc
   37 on-left-right ; left
   39 on-left-right ; right
   38 on-up-down    ; up
   40 on-up-down    ; down
   8  on-del        ; backspace
   46 on-del        ; delete
   32 on-space      ; space
   13 on-enter      ; enter
   })

(defn- on-key-down
  "Handles each key press of caret-input."
  [{:keys [event] :as opts}]
  (let [keycode (.-keyCode event)
        opts (assoc opts :keycode keycode)
        handler (-> keycode
                    keycode->handler)]
    (when handler
      (handler opts))))

(defn- clicked-outside?
  "Is the click event outside of the `dom-node`?"
  [db-root-path event]
  (not (.matches (.-target event) (str "#" (searchbar-events/generate-search-input-id db-root-path) " *"))))

(defn- clicked-suggestion?
  "Is the click event inside of the suggestions dropdown?"
  [event]
  (some? (.closest (.-target event) ".searchbar__suggestion-dropdown")))

(defn- on-caret-input-mount
  "Auto focuses on input and registers global click handler."
  [dom-node on-click] 
  (.focus dom-node)
  (js/document.addEventListener "click" on-click))

(defn- on-caret-input-unmount
  "Deregisters global click handler."
  [input-text on-click]
  (reset! input-text "")
  (js/document.removeEventListener "click" on-click))

(defn- on-change
  "Handles input changes and then executes on-type logic for caret-input"
  [{:keys [event input-text on-type caret-position query]}]
  (let [value (.. event -target -value)]
    (reset! input-text value)
    (on-type {:input value
              :caret-position caret-position
              :search-query @query})))

(defn caret-input
  "Actual input component for typing."
  [{:keys [db-root-path input-text]}]
  (let [dom-node (atom nil)
        focused? (r/atom true)
        keyboard-up-down-arrows-used? (atom false) ;; to understand `keyboard-up-down-arrows-used?` see https://doctorevidence.atlassian.net/browse/SEARCH-168
        on-outside-click #(when (and (clicked-outside? db-root-path %)
                                     (not (clicked-suggestion? %))) 
                            (rf/dispatch [::searchbar-events/clear-suggestions db-root-path])
                            (rf/dispatch [::searchbar-events/set-caret-position db-root-path nil])
                            (reset! input-text "")
                            (reset! focused? false))]
    (r/create-class
     {:component-did-mount #(on-caret-input-mount @dom-node
                                                  on-outside-click)
      :component-will-unmount #(on-caret-input-unmount input-text
                                                       on-outside-click)
      :reagent-render
      (fn [opts]
        (let [opts (assoc opts :keyboard-up-down-arrows-used? keyboard-up-down-arrows-used?)]
          (if @focused?
            [:input
             {:class (bem/be component-name :caret-input)
              :ref #(reset! dom-node %)
              :auto-focus true
              :value @input-text
              :on-change #(on-change (assoc opts :event %))
              :on-key-down #(on-key-down (assoc opts :event %))
              :on-click #(.stopPropagation %)
              :style {:width (caret-input-width @input-text)}}]
            [:div
             {:ref #(reset! dom-node %)
              :class (bem/be component-name :caret-placeholder)
              :on-click #(reset! focused? true)}])))})))

(defn caret-placeholder
  "Placeholder for the caret. It captures onclick events and changes the caret's
   position based on those."
  [{:keys [db-root-path i caret-position]}]
  [:div
   {:class (bem/be component-name :caret-placeholder)
    :on-click #(do 
                 (rf/dispatch [::searchbar-events/set-caret-position db-root-path i]))}])

(defn caret
  "The caret component is used in the search bar to render either input or a
   placeholder, depending on the caret's position."
  [{:keys [db-root-path]}]
  (let [input-text (r/atom "")]
    (fn [{:keys [db-root-path i] :as opts}]
      (let [caret-position @(rf/subscribe [::searchbar-subs/caret-position db-root-path])
            pill-count @(rf/subscribe [::searchbar-subs/pill-count db-root-path]) 
            caret-position (if (= -1 caret-position)
                             pill-count
                             caret-position) 
            opts (assoc opts
                        :caret-position caret-position
                        :input-text input-text 
                        :pill-count pill-count)]
        [:div
         {:class component-name}
         (if (= caret-position i)
           [caret-input opts]
           [caret-placeholder opts])]))))
